// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from 'react';
import './UnsavedChangesModal.scss';

interface UnsavedChangesModalProps {
  isOpen: boolean;
  message: string;
  onSaveAndLeave?: () => void;
  onLeaveWithoutSaving: () => void;
  onCancel: () => void;
  isSaving?: boolean;
  showSaveOption?: boolean;
}

const UnsavedChangesModal: React.FC<UnsavedChangesModalProps> = ({
  isOpen,
  message,
  onSaveAndLeave,
  onLeaveWithoutSaving,
  onCancel,
  isSaving = false,
  showSaveOption = true
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal-backdrop show">
      <div className="modal show d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-exclamation-triangle text-warning me-2"></i>
                Unsaved Changes
              </h5>
            </div>
            <div className="modal-body">
              <p className="mb-0">{message}</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
                disabled={isSaving}
              >
                Cancel
              </button>
              
              <button
                type="button"
                className="btn btn-danger"
                onClick={onLeaveWithoutSaving}
                disabled={isSaving}
              >
                Leave Without Saving
              </button>
              
              {showSaveOption && onSaveAndLeave && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onSaveAndLeave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Saving...</span>
                      </span>
                      Saving...
                    </>
                  ) : (
                    'Save and Leave'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnsavedChangesModal;
